/**
 * RegionsPage — manage regions (Settings → Regions).
 *
 * A region groups areas. Teams can be assigned to a whole region to cover all
 * of its areas for availability.
 */

import { PageHeader } from '@/shared/components';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui';
import type { Region } from '@skewb-works/contracts';
import { Home, MapPinned, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  useCreateRegion,
  useDeleteRegion,
  useRegions,
  useUpdateRegion,
} from '../hooks/useLocations';

export function RegionsPage() {
  const { data: regions = [], isLoading } = useRegions();
  const createMutation = useCreateRegion();
  const [editingId, setEditingId] = useState<number | null>(null);
  const updateMutation = useUpdateRegion(editingId ?? 0);
  const deleteMutation = useDeleteRegion();

  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return regions;
    const q = search.toLowerCase();
    return regions.filter((r) => r.name?.toLowerCase().includes(q));
  }, [regions, search]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  function openCreate() {
    setEditingId(null);
    setName('');
    setDialogOpen(true);
  }

  function openEdit(region: Region) {
    setEditingId(region.id);
    setName(region.name ?? '');
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!name.trim()) return;
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ name });
      } else {
        await createMutation.mutateAsync({ name });
      }
      showSuccessToast(editingId ? 'Region updated.' : 'Region created.');
      setDialogOpen(false);
    } catch (error: any) {
      showErrorToast('Save failed', error?.message || 'An unexpected error occurred.');
    }
  }

  async function handleDelete() {
    if (deleteId === null) return;
    try {
      await deleteMutation.mutateAsync(deleteId);
      showSuccessToast('Region deleted.');
    } catch (error: any) {
      showErrorToast('Delete failed', error?.message || 'An unexpected error occurred.');
    } finally {
      setDeleteId(null);
    }
  }

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Regions"
        subtitle="Group areas into regions for team coverage"
        icon={<MapPinned className="w-8 h-8" style={{ color: '#E84A2B' }} />}
        breadcrumbs={[
          { label: <Home className="w-4 h-4" />, href: '/', current: false },
          { label: 'Settings', href: '/settings', current: false },
          { label: 'Regions', current: true },
        ]}
        actions={
          <Button
            onClick={openCreate}
            style={{ backgroundColor: '#E84A2B' }}
            className="text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add Region
          </Button>
        }
      />

      <div className="px-6 pb-6 flex-1">
        <div className="mb-4 relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search regions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="text-center w-[120px]">Areas</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                    No regions yet.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((region) => (
                  <TableRow key={region.id} className="border-border hover:bg-muted/50">
                    <TableCell className="font-medium text-foreground">{region.name}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{region.area_count}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(region)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => setDeleteId(region.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Region' : 'Add Region'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="region-name">Name *</Label>
            <Input
              id="region-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. North"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!name.trim() || isSaving}
              style={{ backgroundColor: '#E84A2B' }}
              className="text-white"
            >
              {isSaving ? 'Saving...' : editingId ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Region</AlertDialogTitle>
            <AlertDialogDescription>
              This region will be deleted. Its areas are kept but detached from the region.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
